import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useEffect, useMemo, useState } from "react";

import {
  OperationCreateForm,
  OperationFilterOption,
  OperationOptions,
  OperationRecord,
  OperationTab,
} from "../types";

interface OperationCreateDialogProps {
  open: boolean;
  tab: OperationTab;
  options: OperationOptions;
  initialStockRequest?: OperationRecord | null;
  currentUserId?: string;
  loading: boolean;
  onClose: () => void;
  onSubmit: (form: OperationCreateForm) => void;
}

const defaultForm = (tab: OperationTab): OperationCreateForm => ({
  tab,
  hubId: "",
  sourceHubId: "",
  destinationHubId: "",
  employeeId: "",
  stockRequestId: "",
  stockTransferId: "",
  serviceTicketId: "",
  vehicleId: "",
  priority: "MEDIUM",
  labourCharge: 0,
  discountAmount: 0,
  taxAmount: 0,
  paymentMode: "",
  invoiceStatus: "PENDING",
  remarks: "",
  item: {
    sparePartId: "",
    quantity: 1,
    damagedQuantity: 0,
    missingQuantity: 0,
    unitCost: 0,
    remarks: "",
  },
  items: [
    {
      sparePartId: "",
      quantity: 1,
      remarks: "",
    },
  ],
});

const priorities = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];
const invoiceStatuses = ["PENDING", "GENERATED", "PAID", "CANCELLED"];

const OperationCreateDialog = ({
  open,
  tab,
  options,
  initialStockRequest,
  currentUserId = "",
  loading,
  onClose,
  onSubmit,
}: OperationCreateDialogProps) => {
  const [form, setForm] = useState<OperationCreateForm>(defaultForm(tab));

  const selectedRequest =
    options.stockRequests.find((request) => request.id === form.stockRequestId) ||
    initialStockRequest ||
    null;
  const selectedTransfer = options.stockTransfers.find(
    (transfer) => transfer.id === form.stockTransferId
  );

  const requestItems = useMemo(
    () => (((selectedRequest?.raw.items || []) as Record<string, unknown>[]) || []),
    [selectedRequest]
  );
  const selectedRequestHubId = String(
    selectedRequest?.raw.requestHubId ||
      selectedRequest?.raw.hubId ||
      selectedRequest?.warehouseId ||
      ""
  );
  const selectedHubCityId = options.hubs.find((hub) => hub.id === form.hubId)?.cityId;
  const sourceHubOptions = useMemo(() => {
    if (tab !== "transfers" || !requestItems.length) return [];

    const requiredItems = requestItems.map((item) => ({
      sparePartId: String(item.sparePartId || ""),
      quantity: Number(
        item.quantityRequested || item.requestedQuantity || item.quantity || 0
      ),
    }));

    const sourceByHub = new Map<string, OperationFilterOption>();

    options.inventoryStocks
      .filter(
        (stock) =>
          requiredItems.some((item) => item.sparePartId === stock.sparePartId) &&
          stock.warehouseId !== selectedRequestHubId &&
          requiredItems.some(
            (item) =>
              item.sparePartId === stock.sparePartId &&
              stock.availableQuantity >= item.quantity
          )
      )
      .forEach((stock) => {
        sourceByHub.set(stock.warehouseId, {
        id: stock.warehouseId,
        name: `${stock.warehouseLabel} - Available ${stock.availableQuantity}`,
        });
      });

    return Array.from(sourceByHub.values())
      .filter((option) =>
        requiredItems.every((item) =>
          options.inventoryStocks.some(
            (stock) =>
              stock.warehouseId === option.id &&
              stock.sparePartId === item.sparePartId &&
              stock.availableQuantity >= item.quantity
          )
        )
      )
      .sort((a, b) => {
      const stockA = Number(a.name.match(/Available\s+(\d+)/)?.[1] || 0);
      const stockB = Number(b.name.match(/Available\s+(\d+)/)?.[1] || 0);

      return stockB - stockA;
    });
  }, [
    options.inventoryStocks,
    requestItems,
    selectedRequestHubId,
    tab,
  ]);
  const bestSourceHub = sourceHubOptions[0]?.id || "";
  const transferItems = useMemo(
    () => (((selectedTransfer?.raw.items || []) as Record<string, unknown>[]) || []),
    [selectedTransfer]
  );
  const currentAmeId = useMemo(
    () =>
      options.ames.some((ame) => ame.id === currentUserId)
        ? currentUserId
        : options.ames[0]?.id || currentUserId,
    [currentUserId, options.ames]
  );

  useEffect(() => {
    if (!open) return;

    const nextForm = defaultForm(tab);
    nextForm.employeeId = currentAmeId || "";

    if (tab === "transfers" && initialStockRequest) {
      const firstItem = ((initialStockRequest.raw.items || []) as Record<string, unknown>[])[0];
      const destinationHubId = String(
        initialStockRequest.raw.requestHubId ||
          initialStockRequest.raw.hubId ||
          initialStockRequest.warehouseId ||
          ""
      );

      nextForm.stockRequestId = initialStockRequest.id;
      nextForm.destinationHubId = destinationHubId;

      if (firstItem) {
        nextForm.item = {
          ...nextForm.item,
          stockRequestItemId: String(firstItem.id),
          sparePartId: String(firstItem.sparePartId),
          quantity: Number(
            firstItem.quantityRequested ||
              firstItem.requestedQuantity ||
              firstItem.quantity ||
              1
          ),
        };
      }
    }

    setForm(nextForm);
  }, [currentAmeId, initialStockRequest, open, tab]);

  useEffect(() => {
    if (tab !== "transfers" || form.sourceHubId || !bestSourceHub) return;

    setForm((current) => ({
      ...current,
      sourceHubId: bestSourceHub,
    }));
  }, [bestSourceHub, form.sourceHubId, tab]);

  const update = (patch: Partial<OperationCreateForm>) =>
    setForm((current) => ({ ...current, ...patch }));

  const updateItem = (patch: Partial<OperationCreateForm["item"]>) =>
    setForm((current) => ({ ...current, item: { ...current.item, ...patch } }));

  const updateRequestItem = (
    index: number,
    patch: Partial<OperationCreateForm["item"]>
  ) => {
    setForm((current) => {
      const items = current.items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item
      );
      const updatedItem = items[index];

      if (
        patch.sparePartId &&
        index === items.length - 1 &&
        updatedItem.sparePartId
      ) {
        items.push({ sparePartId: "", quantity: 1, remarks: "" });
      }

      return { ...current, items };
    });
  };

  const removeRequestItem = (index: number) => {
    setForm((current) => {
      if (current.items.length === 1) {
        return { ...current, items: [{ sparePartId: "", quantity: 1, remarks: "" }] };
      }

      return { ...current, items: current.items.filter((_, itemIndex) => itemIndex !== index) };
    });
  };

  const handleClose = () => {
    setForm(defaultForm(tab));
    onClose();
  };

  const handleSubmit = () => {
    onSubmit({ ...form, tab, employeeId: form.employeeId || currentAmeId });
    setForm(defaultForm(tab));
  };
  const isTransferBlocked =
    tab === "transfers" &&
    (!form.stockRequestId ||
      !form.sourceHubId ||
      !requestItems.length);
  const isRequestBlocked =
    tab === "requests" &&
    (!form.hubId ||
      !form.items.some((item) => item.sparePartId) ||
      form.items.some(
        (item) => item.sparePartId && Number(item.quantity || 0) <= 0
      ));

  const needsHub = ["requests", "receipts", "issues", "spareUsage"].includes(
    tab
  );
  const needsEmployee = !["requests", "transfers", "invoices"].includes(tab);
  const employeeOptions = ["requests", "transfers", "receipts"].includes(tab)
    ? options.ames
    : options.technicians;
  const needsSpare = ["issues", "spareUsage"].includes(tab);
  const needsTicket = ["issues", "spareUsage", "invoices"].includes(tab);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create {tabLabel(tab)}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={0.5}>
          {needsHub && (
            <Grid item xs={12} md={6}>
              <OptionSelect
                label="Hub"
                value={form.hubId}
                options={options.hubs}
                onChange={(hubId) =>
                  update({ hubId })
                }
              />
            </Grid>
          )}

          {needsEmployee && (
            <Grid item xs={12} md={6}>
              <OptionSelect
                label={employeeOptions === options.ames ? "AME" : "Technician"}
                value={form.employeeId}
                options={employeeOptions}
                onChange={(employeeId) => update({ employeeId })}
              />
            </Grid>
          )}

          {tab === "requests" && (
            <Grid item xs={12} md={6}>
              <EnumSelect
                label="Priority"
                value={form.priority}
                options={priorities}
                onChange={(priority) => update({ priority })}
              />
            </Grid>
          )}

          {tab === "transfers" && (
            <>
              <Grid item xs={12}>
                <RecordSelect
                  label="Stock Request"
                  value={form.stockRequestId}
                  records={options.stockRequests}
                  onChange={(stockRequestId) => {
                    const request = options.stockRequests.find(
                      (row) => row.id === stockRequestId
                    );
                    const firstItem = ((request?.raw.items || []) as Record<string, unknown>[])[0];
                    const destinationHubId = String(
                      request?.raw.requestHubId ||
                        request?.raw.hubId ||
                        request?.warehouseId ||
                        ""
                    );

                    update({
                      stockRequestId,
                      destinationHubId,
                      sourceHubId: "",
                    employeeId: currentAmeId,
                      item: firstItem
                        ? {
                            ...defaultForm(tab).item,
                            stockRequestItemId: String(firstItem.id),
                            sparePartId: String(firstItem.sparePartId),
                            quantity: Number(
                              firstItem.quantityRequested ||
                                firstItem.requestedQuantity ||
                                firstItem.quantity ||
                                1
                            ),
                          }
                        : defaultForm(tab).item,
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <OptionSelect
                  label="Source Hub"
                  value={form.sourceHubId}
                  options={sourceHubOptions}
                  onChange={(sourceHubId) => update({ sourceHubId })}
                  disabled={!form.item.sparePartId}
                  emptyText={
                    form.item.sparePartId
                      ? "No hub has enough available stock"
                      : "Select requested item first"
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <OptionSelect
                  label="Destination Hub"
                  value={form.destinationHubId || selectedRequestHubId}
                  options={options.hubs}
                  onChange={(destinationHubId) => update({ destinationHubId })}
                  disabled={!!selectedRequestHubId}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Requested Spare Parts"
                  value={requestedSparesLabel(requestItems, options.spareParts)}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              {form.stockRequestId && form.sourceHubId && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="info.main" fontWeight={700}>
                    Creating this transfer reserves all listed quantities at the source hub until dispatch or cancellation.
                  </Typography>
                </Grid>
              )}
            </>
          )}

          {tab === "receipts" && (
            <>
              <Grid item xs={12}>
                <RecordSelect
                  label="Stock Transfer"
                  value={form.stockTransferId}
                  records={options.stockTransfers}
                  onChange={(stockTransferId) =>
                    update({
                      stockTransferId,
                      item: defaultForm(tab).item,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <RawItemSelect
                  label="Transfer Item"
                  value={form.item.stockTransferItemId || ""}
                  items={transferItems}
                  onChange={(item) =>
                    updateItem({
                      stockTransferItemId: String(item.id),
                      sparePartId: String(item.sparePartId),
                      quantity: Number(item.quantityDispatched || 1),
                    })
                  }
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <NumberInput
                  label="Damaged"
                  value={form.item.damagedQuantity || 0}
                  onChange={(damagedQuantity) => updateItem({ damagedQuantity })}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <NumberInput
                  label="Missing"
                  value={form.item.missingQuantity || 0}
                  onChange={(missingQuantity) => updateItem({ missingQuantity })}
                />
              </Grid>
            </>
          )}

          {needsTicket && (
            <Grid item xs={12} md={6}>
              <OptionSelect
                label="Service Ticket"
                value={form.serviceTicketId}
                options={options.serviceTickets}
                onChange={(serviceTicketId) => update({ serviceTicketId })}
              />
            </Grid>
          )}

          {tab === "issues" && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Vehicle ID"
                value={form.vehicleId}
                onChange={(event) => update({ vehicleId: event.target.value })}
              />
            </Grid>
          )}

          {needsSpare && (
            <Grid item xs={12} md={8}>
              <OptionSelect
                label="Spare Part"
                value={form.item.sparePartId}
                options={options.spareParts}
                onChange={(sparePartId) => updateItem({ sparePartId })}
              />
            </Grid>
          )}

          {tab === "requests" && (
            <Grid item xs={12}>
              <Stack spacing={1.5}>
                {form.items.map((requestItem, index) => {
                  const selectedSparePartIds = form.items
                    .filter((item, itemIndex) => itemIndex !== index)
                    .map((item) => item.sparePartId)
                    .filter(Boolean);
                  const availableAtOtherHubs = options.inventoryStocks
                    .filter((stock) => {
                      const stockHub = options.hubs.find(
                        (hub) => hub.id === stock.warehouseId
                      );

                      return (
                        stock.sparePartId === requestItem.sparePartId &&
                        stock.warehouseId !== form.hubId &&
                        stock.availableQuantity > 0 &&
                        !!selectedHubCityId &&
                        stockHub?.cityId === selectedHubCityId
                      );
                    })
                    .sort((a, b) => b.availableQuantity - a.availableQuantity);

                  return (
                    <Grid container spacing={2} key={`request-item-${index}`}>
                      <Grid item xs={12} md={7}>
                        <OptionSelect
                          label={`Spare Part ${index + 1}`}
                          value={requestItem.sparePartId}
                          options={options.spareParts.filter(
                            (part) => !selectedSparePartIds.includes(part.id)
                          )}
                          onChange={(sparePartId) =>
                            updateRequestItem(index, { sparePartId })
                          }
                        />
                      </Grid>
                      <Grid item xs={9} md={3}>
                        <NumberInput
                          label="Quantity"
                          value={requestItem.quantity}
                          onChange={(quantity) =>
                            updateRequestItem(index, { quantity })
                          }
                        />
                      </Grid>
                      <Grid item xs={3} md={2} display="flex" alignItems="center" justifyContent="center">
                        <IconButton
                          aria-label={`Remove spare part ${index + 1}`}
                          color="error"
                          onClick={() => removeRequestItem(index)}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Grid>
                      {requestItem.sparePartId && form.hubId && (
                        <Grid item xs={12}>
                          <Stack
                            direction="row"
                            spacing={1}
                            useFlexGap
                            flexWrap="wrap"
                            alignItems="center"
                          >
                            <Typography variant="caption" color="text.secondary" fontWeight={700}>
                              Available at other hubs in this city:
                            </Typography>
                            {availableAtOtherHubs.length ? (
                              availableAtOtherHubs.map((stock) => (
                                <Chip
                                  key={stock.id}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  label={`${stock.warehouseLabel}: ${stock.availableQuantity}`}
                                />
                              ))
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                No available stock at another hub in this city.
                              </Typography>
                            )}
                          </Stack>
                        </Grid>
                      )}
                    </Grid>
                  );
                })}
              </Stack>
            </Grid>
          )}

          {tab !== "invoices" && tab !== "requests" && (
            <Grid item xs={12} md={4}>
              <NumberInput
                label={tab === "spareUsage" ? "Quantity Used" : "Quantity"}
              value={form.item.quantity}
                onChange={(quantity) => updateItem({ quantity })}
                disabled={tab === "transfers"}
              />
            </Grid>
          )}

          {tab === "spareUsage" && (
            <Grid item xs={12} md={4}>
              <NumberInput
                label="Unit Cost"
                value={form.item.unitCost || 0}
                onChange={(unitCost) => updateItem({ unitCost })}
              />
            </Grid>
          )}

          {tab === "invoices" && (
            <>
              <Grid item xs={12} md={4}>
                <NumberInput
                  label="Labour Charge"
                  value={form.labourCharge}
                  onChange={(labourCharge) => update({ labourCharge })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <NumberInput
                  label="Discount"
                  value={form.discountAmount}
                  onChange={(discountAmount) => update({ discountAmount })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <NumberInput
                  label="Tax"
                  value={form.taxAmount}
                  onChange={(taxAmount) => update({ taxAmount })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Payment Mode"
                  value={form.paymentMode}
                  onChange={(event) => update({ paymentMode: event.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EnumSelect
                  label="Invoice Status"
                  value={form.invoiceStatus}
                  options={invoiceStatuses}
                  onChange={(invoiceStatus) => update({ invoiceStatus })}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Remarks"
              value={form.remarks}
              onChange={(event) => update({ remarks: event.target.value })}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button
          variant="contained"
          disabled={loading || isTransferBlocked || isRequestBlocked}
          onClick={handleSubmit}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const tabLabel = (tab: OperationTab) =>
  ({
    requests: "Stock Request",
    transfers: "Stock Transfer",
    receipts: "Goods Receipt",
    issues: "Stock Issue",
    spareUsage: "Service Spare Usage",
    invoices: "Service Invoice",
  }[tab]);

const optionLabel = (option: OperationFilterOption) =>
  option.code ? `${option.name} (${option.code})` : option.name;

const requestedSpareLabel = (
  item: Record<string, unknown> | undefined,
  spareParts: OperationFilterOption[]
) => {
  if (!item) return "";

  const sparePart = item.sparePart as Record<string, unknown> | undefined;
  const sparePartId = String(item.sparePartId || "");
  const option = spareParts.find((part) => part.id === sparePartId);
  const name = String(sparePart?.name || option?.name || sparePartId);
  const code = String(sparePart?.code || option?.code || "");

  return code ? `${name} (${code})` : name;
};

const requestedSparesLabel = (
  items: Record<string, unknown>[],
  spareParts: OperationFilterOption[]
) =>
  items
    .map((item) => {
      const label = requestedSpareLabel(item, spareParts);
      const quantity = Number(
        item.quantityRequested || item.requestedQuantity || item.quantity || 0
      );

      return label ? `${label} x ${quantity}` : "";
    })
    .filter(Boolean)
    .join(", ");

const OptionSelect = ({
  label,
  value,
  options,
  onChange,
  disabled = false,
  emptyText = "No options available",
}: {
  label: string;
  value: string;
  options: OperationFilterOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  emptyText?: string;
}) => (
  <FormControl fullWidth>
    <InputLabel>{label}</InputLabel>
    <Select
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
    >
      {!options.length && (
        <MenuItem value="" disabled>
          {emptyText}
        </MenuItem>
      )}
      {options.map((option) => (
        <MenuItem value={option.id} key={option.id}>
          {optionLabel(option)}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const RecordSelect = ({
  label,
  value,
  records,
  onChange,
}: {
  label: string;
  value: string;
  records: Array<{ id: string; code: string; title: string; status: string }>;
  onChange: (value: string) => void;
}) => (
  <FormControl fullWidth>
    <InputLabel>{label}</InputLabel>
    <Select label={label} value={value} onChange={(event) => onChange(event.target.value)}>
      {records.map((record) => (
        <MenuItem value={record.id} key={record.id}>
          <Stack>
            {record.code}
            <small>{record.status} • {record.title}</small>
          </Stack>
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const RawItemSelect = ({
  label,
  value,
  items,
  onChange,
}: {
  label: string;
  value: string;
  items: Record<string, unknown>[];
  onChange: (item: Record<string, unknown>) => void;
}) => (
  <FormControl fullWidth>
    <InputLabel>{label}</InputLabel>
    <Select
      label={label}
      value={value}
      onChange={(event) => {
        const item = items.find((row) => String(row.id) === event.target.value);
        if (item) onChange(item);
      }}
    >
      {items.map((item) => {
        const sparePart = item.sparePart as Record<string, unknown> | undefined;

        return (
          <MenuItem value={String(item.id)} key={String(item.id)}>
            {String(sparePart?.name || item.sparePartId)} • Qty{" "}
            {String(item.quantityRequested || item.quantityDispatched || item.quantity || 0)}
          </MenuItem>
        );
      })}
    </Select>
  </FormControl>
);

const EnumSelect = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) => (
  <FormControl fullWidth>
    <InputLabel>{label}</InputLabel>
    <Select label={label} value={value} onChange={(event) => onChange(event.target.value)}>
      {options.map((option) => (
        <MenuItem value={option} key={option}>
          {option}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const NumberInput = ({
  label,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}) => (
  <TextField
    fullWidth
    type="number"
    label={label}
    value={value}
    disabled={disabled}
    onChange={(event) => onChange(Number(event.target.value))}
  />
);

export default OperationCreateDialog;
